<script lang="ts">
  import { format, addDays, parseISO, addYears } from 'date-fns';
  import { userService } from '$lib/services/user.service';
  import { toasts } from '$lib/stores/toastStores';
  import { dialogStates, closeDialog } from '$lib/stores/dialogStores';
  import { user as userStore } from '$lib/stores';
  import { get } from 'svelte/store';
  import { translate } from "$lib/i18n";
  import Button from "$lib/elements/Button.svelte";

  // Define the user prop if it's passed in
  export let userData = get(userStore);

  let loading = false;
  let selectedPeriod = 1; // Default to 1 day (24 hours)
  let contentType = false; // Controls which view to show (confirmation or success)
  let modalName = 'SelfExcludeModal';

  // Define exclusion periods in days
  const exclusionPeriods = [
    { value: 1, label: '24 Hours' },
    { value: 7, label: '7 Days' },
    { value: 30, label: '30 Days' },
    { value: 90, label: '90 Days' },
    { value: 180, label: '180 Days' },
    { value: 365, label: '1 Year' },
    { value: 1825, label: '5 Years' },
    { value: 0, label: 'Permanent' } // 0 means permanent exclusion
  ];

  // For permanent exclusion, we'll use 50 years (18250 days)
  const PERMANENT_EXCLUSION_DAYS = 365 * 50;

  function getExclusionEndDate(days: number): string {
    if (days === 0) return 'Permanent';

    const now = new Date();
    return format(addDays(now, days), 'MMM dd, yyyy');
  }

  function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';

    try {
      const date = parseISO(dateString);

      // Check if this is likely a "permanent" exclusion (more than 40 years in the future)
      const fortyYearsFromNow = addYears(new Date(), 40);
      if (date > fortyYearsFromNow) {
        return 'Permanent';
      }

      return format(date, 'yyyy-MM-dd HH:mm');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  function close() {
    window.location.reload();
    closeDialog(modalName);
  }

  function setSelfExclusion() {
    // If permanent exclusion is selected (0), use 50 years instead
    const exclusionDays = selectedPeriod === 0 ? PERMANENT_EXCLUSION_DAYS : selectedPeriod;

    // Create the payload with the exact format expected by the API
    const data = {
      exclusionDays: exclusionDays
    };

    loading = true;

    userService.setSelfExclusion(data)
            .then((response: any) => {
              if (response) {
                contentType = true; // Switch to success view

                // Fetch updated user data
                userService.getUser()
                        .then((userResponse: any) => {
                          if (userResponse && userResponse.data && userResponse.data.data) {
                            userData = userResponse.data.data;
                          }
                        })
                        .catch(err => {
                          console.error("Error fetching user data:", err);
                        });
              }
            })
            .catch(err => {
              console.error("Error setting self-exclusion:", err);
              toasts.send('Failed to set self-exclusion', 'error');
            })
            .finally(() => {
              loading = false;
            });
  }
</script>

<div class="flex justify-center items-center flex-col my-1 text-white">
  <div class="flex justify-center items-center border-b border-b-primary-lightBlue mb-4 w-full">
    <span class="text-white text-xl">{$translate('Self Exclude')}</span>
  </div>

  {#if !contentType}
    <!-- Confirmation View -->
    <div class="p-5 w-full">
      <p class="mb-4">{$translate('Self-exclusion allows you to take a break from gambling. During this period, you won\'t be able to access your account or place any bets.')}</p>

      <div class="bg-yellow-900/20 border border-yellow-700/20 rounded-lg p-4 mb-4">
        <p class="text-yellow-500 text-sm">
          {$translate('Warning: This action cannot be reversed until the selected period expires.')}
        </p>
      </div>

      <div class="mb-4">
        <label class="block text-gray-300 mb-2">{$translate('Select Exclusion Period')}</label>
        <select
                bind:value={selectedPeriod}
                class="w-full rounded-md bg-gray-800 border-gray-700 text-gray-300 p-2 mb-4"
        >
          {#each exclusionPeriods as period}
            <option value={period.value}>{$translate(period.label)}</option>
          {/each}
        </select>

        {#if selectedPeriod !== 0}
          <div class="bg-gray-800/50 rounded-lg p-4 mb-4">
            <p class="text-sm text-gray-400">{$translate('Your account will be blocked until:')}</p>
            <p class="text-lg font-medium">{getExclusionEndDate(selectedPeriod)}</p>
          </div>
        {:else}
          <div class="bg-gray-800/50 rounded-lg p-4 mb-4">
            <p class="text-sm text-gray-400">{$translate('Your account will be blocked:')}</p>
            <p class="text-lg font-medium">{$translate('Permanently')}</p>
          </div>
        {/if}
      </div>
    </div>

    <div class="flex flex-row w-full gap-2 p-5">
      <Button clas="w-full" on:click={() => setSelfExclusion()} {loading}>{$translate('Confirm')}</Button>
      <button
              class="w-full bg-opacity-90 bg-primary-blue rounded-full border border-primary-fire text-white px-7 hover:bg-primary-lightBlue"
              on:click={() => close()}
      >
        {$translate('Cancel')}
      </button>
    </div>
  {:else}
    <!-- Success View -->
    <div class="p-5">
      <p>{$translate('Self Exclusion Activated')}</p>
    </div>

    <div class="pb-5">
      <p>
        {$translate('Expiration Date:')}
        {userData && userData.selfExclusionExpiresAt
                ? formatDate(userData.selfExclusionExpiresAt)
                : $translate('Processing...')}
      </p>
    </div>

    <Button clas="w-3/5" on:click={() => close()}>{$translate('OK')}</Button>
  {/if}
</div>